<template>
  <vx-card>
    <div class="header-block">
      <h4>Log de acesso a dado pessoal</h4>
      <p>
        Responde a pergunta que abre toda investigação de incidente: <strong>quem viu o
        CPF, o dado bancário ou o motivo médico deste titular, e quando</strong>
        (LGPD, Art. 46 e 48). Registra acesso de operador — o titular lendo o próprio
        dado não entra aqui, senão a tabela vira ruído.
      </p>
    </div>

    <div class="filters-row">
      <vs-select v-model="filters.category" label="Categoria" class="filter-field" @input="load">
        <vs-select-item :value="''" text="Todas" />
        <vs-select-item v-for="(label, key) in categoryLabels" :key="key" :value="key" :text="label" />
      </vs-select>

      <vs-input
        v-model="filters.subject_user_id"
        label="ID do titular"
        placeholder="Ex.: 42"
        class="filter-field"
        @keyup.enter="load"
      />

      <vs-input
        v-model="filters.actor_id"
        label="ID de quem acessou"
        placeholder="Ex.: 7"
        class="filter-field"
        @keyup.enter="load"
      />

      <vs-button size="small" @click="load">Filtrar</vs-button>
    </div>

    <div v-if="loading" class="state-message">Carregando…</div>

    <div v-else-if="!rows.length" class="state-message">
      Nenhum acesso registrado com estes filtros.
    </div>

    <table v-else class="privacy-table">
      <thead>
        <tr>
          <th>Quando</th>
          <th>Quem acessou</th>
          <th>Titular</th>
          <th>Categoria</th>
          <th>Ação</th>
          <th>Rota</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.id">
          <td>{{ formatDateTime(row.created_at) }}</td>
          <td>
            <div class="subject">
              <strong>{{ row.actor ? row.actor.name : '—' }}</strong>
              <span v-if="row.actor_legal_entity_id">Clube #{{ row.actor_legal_entity_id }}</span>
            </div>
          </td>
          <td>
            <div class="subject">
              <strong>{{ row.subject ? row.subject.name : '—' }}</strong>
              <span>{{ row.subject ? row.subject.email : '' }}</span>
            </div>
          </td>
          <td>
            <span class="pill" :class="row.category">{{ row.category_label }}</span>
          </td>
          <td>{{ row.action === 'export' ? 'Exportação' : 'Leitura' }}</td>
          <td class="route-cell">{{ row.route }}</td>
        </tr>
      </tbody>
    </table>

    <div v-if="pagination.last_page > 1" class="pagination-row">
      <vs-button size="small" type="border" :disabled="page <= 1" @click="go(page - 1)">Anterior</vs-button>
      <span>Página {{ pagination.current_page }} de {{ pagination.last_page }}</span>
      <vs-button size="small" type="border" :disabled="page >= pagination.last_page" @click="go(page + 1)">Próxima</vs-button>
    </div>
  </vx-card>
</template>

<script>
import PrivacyService from '@/services/api/PrivacyService'

export default {
  name: 'PrivacyAccessLogsIndex',
  data() {
    return {
      loading: true,
      rows: [],
      page: 1,
      pagination: { current_page: 1, last_page: 1 },
      filters: { category: '', subject_user_id: '', actor_id: '' },
      categoryLabels: {
        document: 'CPF / CNPJ',
        bank: 'Dados bancários',
        pix: 'Chave PIX',
        health: 'Motivo médico',
        export: 'Exportação de dados',
        contact: 'Contato (e-mail/telefone)'
      }
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const response = await PrivacyService.build(this.$vs).accessLogGrid({
          category: this.filters.category,
          subject_user_id: this.filters.subject_user_id,
          actor_id: this.filters.actor_id,
          page: this.page
        })

        this.rows = response.data || []
        this.pagination = response
      } catch (e) {
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    go(page) {
      this.page = page
      this.load()
    },
    formatDateTime(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('pt-BR')
    }
  }
}
</script>

<style lang="scss" scoped>
.header-block {
  margin-bottom: 1.25rem;

  h4 { margin: 0 0 0.35rem; }

  p {
    margin: 0;
    font-size: 0.82rem;
    color: #6e6b7b;
    line-height: 1.5;
    max-width: 760px;
  }
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.filter-field {
  min-width: 180px;
}

.state-message {
  padding: 2rem 0;
  text-align: center;
  color: #6e6b7b;
}

.privacy-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  th {
    text-align: left;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #6e6b7b;
    padding: 0.5rem;
    border-bottom: 1px solid #ebe9f1;
  }

  td {
    padding: 0.65rem 0.5rem;
    border-bottom: 1px solid #f5f4f7;
    vertical-align: middle;
  }
}

.subject {
  display: flex;
  flex-direction: column;

  span {
    font-size: 0.72rem;
    color: #6e6b7b;
  }
}

.route-cell {
  font-family: monospace;
  font-size: 0.75rem;
  color: #6e6b7b;
}

.pill {
  display: inline-block;
  padding: 0.15rem 0.65rem;
  border-radius: 1rem;
  font-size: 0.72rem;
  font-weight: 600;
  background: #eceff1;
  color: #546e7a;

  /* Saúde é a categoria de maior risco — destacada de propósito. */
  &.health { background: #ffebee; color: #c62828; }
  &.document { background: #fff3e0; color: #b26a00; }
  &.bank,
  &.pix { background: #e3f2fd; color: #1565c0; }
  &.export { background: #ede7f6; color: #5e35b1; }
}

.pagination-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.25rem;
  font-size: 0.82rem;
  color: #6e6b7b;
}
</style>
